/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/api/fields.js
 *
 * Purpose: React Query hook for loading field configuration grouped by division, reshaped
 *     for portal consumption.
 *
 * Scope: Per-division field configuration used to drive dynamic form fields across order,
 *     sample, test, and other entity forms in the portal.
 *
 * Tags: customer-portal, field-config
 *
 * Overview: `useFieldsByDivision` fetches the raw field configuration list and, via the
 *     query's `select` option, transforms it into a map keyed by division ID. For each
 *     division it picks only the entity types relevant to the portal, strips out
 *     LIMS-only/unused attributes (`hidden`, `required`, `order`, etc.), camelizes the
 *     remaining field objects, and separately records which entities have an "attachments"
 *     dummy field so upload UI can be shown conditionally. Per-Assay field screens are kept
 *     alongside, limited to the fields whose Order Request toggle is on. The query is cached
 *     indefinitely (`staleTime`/`cacheTime` set to `Infinity`) since field configuration rarely
 *     changes within a session.
 *
 * Exports: useFieldsByDivision
 */

import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { useQuery } from 'react-query';
import { EntityType, QueryKeys } from 'shared/constants/entityTypes';
import { AddlFieldType } from 'shared/constants/fields';
import { Fields } from 'shared/utils/api';
import { isNotNil } from 'shared/utils/functions';
import { camelize } from 'shared/utils/string';

const entityName = QueryKeys.DIVISION;

// Field attributes relevant only for LIMS, or no longer used in the frontend. Stripped from both the
// per-division configs and the per-Assay screens so they carry the same shape into getFields.
const FIELD_OMIT_LIST = [
  'hidden',
  'global_hidden',
  'portal_hidden',
  'portal_global_hidden',
  'required',
  'reason_required',
  'order',
  'order_receiving',
  'default_value',
];

function getAttachmentEntities(fields) {
  return Object.entries(fields)
    .filter(([_, fields]) =>
      fields.some(
        (field) =>
          field.orderRequestField &&
          field.fieldType === AddlFieldType.DUMMY &&
          field.name === 'attachments',
      ),
    )
    .map(([entity]) => entity);
}

function mapFieldConfigByDivisionId(divisions) {
  const pickList = [
    EntityType.ORDER,
    EntityType.SAMPLE,
    EntityType.TEST,
    EntityType.SOURCE,
    EntityType.PROJECT,
    EntityType.CUSTOMER,
    EntityType.CONTACT,
    EntityType.ASSAY,
    EntityType.PANEL,
    EntityType.INVOICE,
  ];
  return divisions.reduce((acc, div) => {
    const fieldsByEntity = mapValues(pick(div.field_config, pickList), (arr) =>
      camelize(arr.map((c) => omit(c, FIELD_OMIT_LIST))),
    );
    return {
      ...acc,
      [div.id]: {
        fields: fieldsByEntity,
        attachmentEntities: getAttachmentEntities(fieldsByEntity),
      },
    };
  }, {});
}

// Reshape the per-Assay TEST field screens (M2, 86ba83wd3 §5.3) into
// `{ [assayId]: [camelized TEST field] }`, applying the same camelize/omit the division
// configs get so the assay fields drop into getFields identically. A screen field shows on the
// order request form only when its Order Request toggle is on, the same rule the division's
// default TEST fields follow. A screen with none on still replaces the default set, so the Assay
// gets an empty list rather than falling back to the division's fields.
function mapAssayFieldConfigs(assayFieldConfigs) {
  return Object.entries(assayFieldConfigs ?? {}).reduce((acc, [assayId, config]) => {
    const testFields = (config?.[EntityType.TEST] ?? []).filter((c) => c.order_request_field);
    return { ...acc, [assayId]: camelize(testFields.map((c) => omit(c, FIELD_OMIT_LIST))) };
  }, {});
}

// The /v1/fields body carries the divisions array under `data` plus, when Field Screens are on,
// a sibling `assay_field_configs` map. Keep both (the previous select dropped the sibling).
function mapFieldConfig(res) {
  return {
    byDivision: mapFieldConfigByDivisionId(res?.data ?? []),
    byAssay: mapAssayFieldConfigs(res?.assay_field_configs),
  };
}

export function useFieldsByDivision() {
  const queryKey = [entityName];
  const queryFn = () => Fields.byDivision().then(({ data: res }) => res);
  const { data, ...queryResult } = useQuery({
    queryKey,
    queryFn,
    select: mapFieldConfig,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  return {
    data: isNotNil(data?.byDivision) ? data.byDivision : {},
    assayFields: isNotNil(data?.byAssay) ? data.byAssay : {},
    ...queryResult,
  };
}
